import React, { useRef, useState } from 'react';
import { Button, Alert } from '../ui';
import projectService from '../../services/projectService';

const TASK_OPTIONS = [
  ['negative', 'Negative / complaints'],
  ['brand_mentions', 'Brand mentions'],
  ['product_mentions', 'Product / service mentions'],
  ['questions', 'User questions'],
  ['recommendations', 'Recommendations'],
  ['comparisons', 'Comparisons'],
  ['competitors', 'Competitors'],
  ['demand', 'Product / service demand'],
  ['reputation_risks', 'Reputation risks'],
  ['other', 'Other'],
];

const initialProject = (project) => ({
  brand: '',
  products_services: '',
  product_models: '',
  geography: '',
  competitors: '',
  additional_context: project?.activity_description || '',
});

const KeywordGenerationAgent = ({ project, onApply }) => {
  const [form, setForm] = useState(() => initialProject(project));
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [otherTask, setOtherTask] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const generationVersion = useRef(0);

  const invalidatePreview = () => {
    generationVersion.current += 1;
    setResult(null);
  };

  const updateField = (event) => {
    invalidatePreview();
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const toggleTask = (task) => {
    invalidatePreview();
    setSelectedTasks((current) => (
      current.includes(task) ? current.filter((item) => item !== task) : [...current, task]
    ));
  };

  const generate = async () => {
    const version = generationVersion.current;
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const response = await projectService.generateProjectKeywords(project.id, {
        project: form,
        monitoring_tasks: selectedTasks,
        other_task: otherTask,
      });
      if (generationVersion.current === version) setResult(response.data);
    } catch (requestError) {
      if (generationVersion.current === version) {
        setError(requestError?.response?.data?.error || requestError.message);
      }
    } finally {
      setGenerating(false);
    }
  };

  const applyPlan = (plan) => {
    const taskLabel = TASK_OPTIONS.find(([value]) => value === plan.task)?.[1] || plan.task;
    onApply({
      name: taskLabel,
      primary_keywords: plan.primary_keywords.join('\n'),
      secondary_keywords: plan.secondary_groups
        .flatMap((group) => group.keywords)
        .map((keyword) => `+${keyword}`)
        .join('\n'),
      minus_words: result.minus_words.join('\n'),
    });
  };

  return (
    <section className="mb-6 rounded-lg border border-primary-200 bg-primary-50 p-4" aria-label="AI keyword generation">
      <h3 className="text-sm font-medium text-gray-900">AI keyword generation agent</h3>
      <p className="mt-1 text-xs text-gray-600">
        Define the monitoring object first, then select only the tasks you need. Generation returns a reviewable preview.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor="keyword-agent-brand" className="block text-xs font-medium text-gray-700">Brand / company</label>
          <input id="keyword-agent-brand" name="brand" value={form.brand} onChange={updateField} className="mt-1 block w-full rounded-md border-gray-300 text-sm" />
        </div>
        <div>
          <label htmlFor="keyword-agent-models" className="block text-xs font-medium text-gray-700">Products / models</label>
          <input id="keyword-agent-models" name="product_models" value={form.product_models} onChange={updateField} className="mt-1 block w-full rounded-md border-gray-300 text-sm" placeholder="Only explicitly monitored models" />
        </div>
        <div>
          <label htmlFor="keyword-agent-products" className="block text-xs font-medium text-gray-700">Products / services</label>
          <input id="keyword-agent-products" name="products_services" value={form.products_services} onChange={updateField} className="mt-1 block w-full rounded-md border-gray-300 text-sm" />
        </div>
        <div>
          <label htmlFor="keyword-agent-geography" className="block text-xs font-medium text-gray-700">Geography</label>
          <input id="keyword-agent-geography" name="geography" value={form.geography} onChange={updateField} className="mt-1 block w-full rounded-md border-gray-300 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="keyword-agent-competitors" className="block text-xs font-medium text-gray-700">Competitors needed for selected tasks</label>
          <input id="keyword-agent-competitors" name="competitors" value={form.competitors} onChange={updateField} className="mt-1 block w-full rounded-md border-gray-300 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="keyword-agent-context" className="block text-xs font-medium text-gray-700">Additional project context</label>
          <textarea id="keyword-agent-context" name="additional_context" rows={2} value={form.additional_context} onChange={updateField} className="mt-1 block w-full rounded-md border-gray-300 text-sm" />
        </div>
      </div>

      <fieldset className="mt-3">
        <legend className="text-xs font-medium text-gray-700">Monitoring tasks</legend>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {TASK_OPTIONS.map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={selectedTasks.includes(value)} onChange={() => toggleTask(value)} className="rounded border-gray-300 text-primary-600" />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {selectedTasks.includes('other') && (
        <div className="mt-3">
          <label htmlFor="keyword-agent-other" className="block text-xs font-medium text-gray-700">Custom monitoring task</label>
          <textarea id="keyword-agent-other" rows={2} value={otherTask} onChange={(event) => {
            invalidatePreview();
            setOtherTask(event.target.value);
          }} className="mt-1 block w-full rounded-md border-gray-300 text-sm" />
        </div>
      )}

      <div className="mt-3">
        <Button type="button" variant="primary" onClick={generate} isLoading={generating} disabled={selectedTasks.length === 0}>
          Generate keyword preview
        </Button>
      </div>

      {error && <Alert variant="error" className="mt-3">{error}</Alert>}

      {result && (
        <div className="mt-4 rounded-md border border-gray-200 bg-white p-3">
          <p className="text-xs font-medium text-gray-700">Nothing is saved automatically. Review each plan and explicitly apply it to the category form below.</p>
          <div className="mt-3 space-y-3">
            {result.task_plans.map((plan) => (
              <article key={plan.task} className="rounded border border-gray-200 p-3">
                <h4 className="text-sm font-medium text-gray-900">{TASK_OPTIONS.find(([value]) => value === plan.task)?.[1] || plan.task}</h4>
                <div className="mt-2 text-xs text-gray-700">
                  <span className="font-medium">Primary:</span> <span>{plan.primary_keywords.join(', ')}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {plan.secondary_groups.map((group) => (
                    <div key={group.name} className="text-xs text-gray-700">
                      <span className="font-medium">{group.name}:</span> {group.keywords.join(', ')}
                    </div>
                  ))}
                </div>
                <Button type="button" variant="secondary" className="mt-3" onClick={() => applyPlan(plan)}>
                  Use in category form
                </Button>
              </article>
            ))}
          </div>

          <details className="mt-3 text-xs text-gray-700">
            <summary className="cursor-pointer font-medium">Minus exclusions ({result.minus_words.length})</summary>
            <p className="mt-2 whitespace-pre-wrap">{result.minus_words.join(', ')}</p>
          </details>

          {result.recommendations.length > 0 && (
            <div className="mt-3 text-xs text-gray-700">
              <span className="font-medium">Advisory recommendations:</span>
              <ul className="mt-1 list-disc pl-5">{result.recommendations.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          )}

          <details className="mt-3 text-xs text-gray-700">
            <summary className="cursor-pointer font-medium">Agent self-check</summary>
            {Object.entries(result.self_check).map(([name, findings]) => (
              <div key={name} className="mt-2">
                <span className="font-medium">{name}:</span>
                <ul className="list-disc pl-5">{findings.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            ))}
          </details>
        </div>
      )}
    </section>
  );
};

export default KeywordGenerationAgent;
